import { useNavigate } from "@tanstack/react-router";
import { ArrowLeft, CheckCircle2, Hand, Loader2, Undo2, XCircle } from "lucide-react";
import { useCallback, useEffect, useState, type ReactNode } from "react";

import type { TVettingJobDetail } from "@/api/investorVetting";
import { investorVettingService } from "@/api/investorVetting";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Textarea } from "@/components/ui/Textarea";
import { useToast } from "@/components/ui/Toast";
import { useStore } from "@/store";
import { extractErrorMessage } from "@/utils";
import { formatLocaleDate } from "@/utils/date";

const DECLINE_REASON_MAX = 2000;

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:gap-4">
      <dt className="w-40 shrink-0 text-sm font-medium text-muted-foreground">{label}</dt>
      <dd className="text-sm text-foreground">{children}</dd>
    </div>
  );
}

type TOnboardingJobDetailPageProps = {
  jobId: string;
  variant?: "sales" | "admin";
};

export function OnboardingJobDetailPage({ jobId, variant = "sales" }: TOnboardingJobDetailPageProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const profile = useStore((state) => state.profile);

  const [job, setJob] = useState<TVettingJobDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [isActing, setIsActing] = useState(false);
  const [onboardOpen, setOnboardOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState("");

  const backTo = () =>
    variant === "admin"
      ? navigate({ to: "/app/admin/investor-onboarding-jobs" })
      : navigate({ to: "/app/sales/onboarding-jobs" });

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    const { data, error } = await investorVettingService.getJob(jobId);
    if (error) {
      setLoadError(await extractErrorMessage(error));
    } else if (data) {
      setJob(data.data);
    }
    setIsLoading(false);
  }, [jobId]);

  useEffect(() => {
    void load();
  }, [load]);

  const runAction = async (action: () => ReturnType<typeof investorVettingService.claimJob>, successTitle: string) => {
    setIsActing(true);
    const { data, error } = await action();
    if (error) {
      toast({ variant: "destructive", title: "Action failed", description: await extractErrorMessage(error) });
    } else if (data) {
      toast({ title: successTitle });
      setJob(data.data);
    }
    setIsActing(false);
  };

  const handleDecline = async () => {
    const reason = declineReason.trim();
    if (!reason) {
      toast({ variant: "destructive", title: "A reason is required to decline" });
      return;
    }
    setIsActing(true);
    const { data, error } = await investorVettingService.declineInvestor(jobId, reason);
    if (error) {
      toast({ variant: "destructive", title: "Failed to decline", description: await extractErrorMessage(error) });
    } else if (data) {
      toast({ title: "Investor declined", description: "The investor has been sent a neutral notification." });
      setJob(data.data);
      setDeclineOpen(false);
      setDeclineReason("");
    }
    setIsActing(false);
  };

  const isMine = Boolean(job?.claimed_by && profile && job.claimed_by === profile.id);
  const canClaim = job?.status === "pending" || (job?.status === "auto_onboarded" && !job.claimed_by);
  const canDecide = job?.status === "claimed" && job.contact_details_visible;
  const canRelease = Boolean(job?.claimed_by) && (isMine || variant === "admin");

  return (
    <div className="h-full overflow-y-auto bg-background p-4 sm:p-6">
      <div className="mx-auto max-w-4xl space-y-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void backTo()}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to job pool
        </Button>

        {isLoading && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading job…
          </div>
        )}
        {loadError && <p className="text-sm text-destructive">{loadError}</p>}

        {job && (
          <>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-foreground">{job.investor_name}</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  Investor onboarding job · created {formatLocaleDate(new Date(job.created_at))}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {canClaim && (
                  <Button
                    onClick={() => void runAction(() => investorVettingService.claimJob(jobId), "Job claimed")}
                    disabled={isActing}
                  >
                    <Hand className="mr-2 h-4 w-4" />
                    Take ownership
                  </Button>
                )}
                {canRelease && job.status === "claimed" && (
                  <Button
                    variant="outline"
                    onClick={() => void runAction(() => investorVettingService.releaseJob(jobId), "Job released")}
                    disabled={isActing}
                  >
                    <Undo2 className="mr-2 h-4 w-4" />
                    Release
                  </Button>
                )}
                {canDecide && (
                  <>
                    <Button
                      onClick={() => setOnboardOpen(true)}
                      disabled={isActing}
                    >
                      <CheckCircle2 className="mr-2 h-4 w-4" />
                      Onboard
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => setDeclineOpen(true)}
                      disabled={isActing}
                    >
                      <XCircle className="mr-2 h-4 w-4" />
                      Decline
                    </Button>
                  </>
                )}
              </div>
            </div>

            <div className="rounded-lg border border-border bg-card p-4">
              <h2 className="mb-3 text-sm font-semibold text-foreground">Job</h2>
              <dl className="space-y-2">
                <DetailRow label="Status">
                  <Badge
                    className={
                      job.status === "declined" ? "border-destructive/30 bg-destructive/10 text-destructive" : undefined
                    }
                  >
                    {job.status.replace("_", "-")}
                  </Badge>
                </DetailRow>
                <DetailRow label="Source">{job.source === "invited" ? "Invited" : "Self-registered"}</DetailRow>
                <DetailRow label="SLA deadline">
                  {job.sla_expired ? (
                    <Badge className="border-destructive/30 bg-destructive/10 text-destructive">
                      Expired {formatLocaleDate(new Date(job.sla_deadline))}
                    </Badge>
                  ) : (
                    formatLocaleDate(new Date(job.sla_deadline))
                  )}
                </DetailRow>
                <DetailRow label="Owner">{job.claimed_by_name ?? "Unclaimed"}</DetailRow>
                {job.company_name && <DetailRow label="Company">{job.company_name}</DetailRow>}
                {job.decided_at && <DetailRow label="Decided">{formatLocaleDate(new Date(job.decided_at))}</DetailRow>}
                {job.decline_reason && <DetailRow label="Decline reason">{job.decline_reason}</DetailRow>}
              </dl>
            </div>

            <div className="rounded-lg border border-border bg-card p-4">
              <h2 className="mb-3 text-sm font-semibold text-foreground">Contact details</h2>
              {job.contact_details_visible ? (
                <dl className="space-y-2">
                  <DetailRow label="Email">{job.investor_email}</DetailRow>
                  <DetailRow label="Phone">{job.investor_phone ?? "—"}</DetailRow>
                </dl>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Take ownership of this job to see the investor's contact details.
                </p>
              )}
            </div>

            <div className="rounded-lg border border-border bg-card p-4">
              <h2 className="mb-3 text-sm font-semibold text-foreground">Synopsis</h2>
              <p className="whitespace-pre-wrap text-sm text-foreground">{job.synopsis ?? "No synopsis provided."}</p>
            </div>
          </>
        )}

        <ConfirmDialog
          open={onboardOpen}
          onOpenChange={setOnboardOpen}
          title="Onboard this investor?"
          description="They will receive an email with a link to continue their registration, and gain access to investor features."
          confirmLabel="Onboard"
          variant="default"
          isConfirming={isActing}
          onConfirm={async () => {
            await runAction(() => investorVettingService.onboardInvestor(jobId), "Investor onboarded");
            setOnboardOpen(false);
          }}
        />

        <Dialog
          open={declineOpen}
          onOpenChange={isActing ? undefined : setDeclineOpen}
        >
          <DialogContent className="sm:max-w-[480px]">
            <DialogHeader>
              <DialogTitle>Decline this investor?</DialogTitle>
              <DialogDescription>
                The reason is kept for internal record only — the investor receives a neutral notification without it.
              </DialogDescription>
            </DialogHeader>
            <Textarea
              value={declineReason}
              onChange={(event) => setDeclineReason(event.target.value)}
              maxLength={DECLINE_REASON_MAX}
              rows={4}
              placeholder="Reason for declining (required)"
            />
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="outline"
                onClick={() => setDeclineOpen(false)}
                disabled={isActing}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => void handleDecline()}
                disabled={isActing || !declineReason.trim()}
              >
                {isActing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Decline investor
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
